import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { seoIndexCoverage, seoRebuildIndexCoverage } from "@/lib/seo/seo-os.functions";
import { SeoPage, Stat, Panel, Pill, Loading, Empty, DataTable, num } from "@/components/admin/seo/SeoUi";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/seo/index-coverage")({
  head: () => ({ meta: [{ title: "Index coverage · Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: CoveragePage,
});

const LABEL: Record<string, { text: string; tone: string }> = {
  indexed: { text: "Showing in Google", tone: "good" },
  crawlable_not_seen: { text: "Never shown", tone: "warn" },
  blocked: { text: "Blocked from search", tone: "neutral" },
  error: { text: "Page error", tone: "bad" },
  not_in_sitemap: { text: "Missing from sitemap", tone: "warn" },
  unknown: { text: "Unknown — needs Search Console", tone: "neutral" },
};

function CoveragePage() {
  const load = useServerFn(seoIndexCoverage);
  const rebuild = useServerFn(seoRebuildIndexCoverage);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["seo-coverage"], queryFn: () => load() });
  const d = q.data as any;
  const rows = (d?.rows ?? []) as any[];

  const refresh = useMutation({
    mutationFn: () => rebuild({}),
    onSuccess: (r: any) => {
      toast.success(`Coverage rebuilt for ${r.rows} pages.`);
      qc.invalidateQueries({ queryKey: ["seo-coverage"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not rebuild coverage."),
  });

  return (
    <SeoPage
      title="Index coverage"
      description="Which of your pages Google has actually shown to people, which it has never shown, and which are blocked or broken."
      actions={
        <Button onClick={() => refresh.mutate()} disabled={refresh.isPending}>
          {refresh.isPending ? "Rebuilding…" : "Rebuild"}
        </Button>
      }
    >
      {q.isLoading ? (
        <Loading />
      ) : rows.length === 0 ? (
        <Empty>Nothing yet — run a site scan first, then rebuild coverage.</Empty>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-4">
            {Object.entries(d.counts as Record<string, number>).map(([state, count]) => (
              <Stat key={state} label={LABEL[state]?.text ?? state} value={num(count)} />
            ))}
          </div>
          <Panel title="Every page" subtitle="Sorted by how often it appears in search.">
            <DataTable
              head={["Page", "State", "Impressions", "Clicks", "Detail"]}
              rows={rows.slice(0, 300).map((r) => [
                r.path,
                <Pill key={r.path} tone={LABEL[r.state]?.tone ?? "neutral"}>
                  {LABEL[r.state]?.text ?? r.state}
                </Pill>,
                num(r.gsc_impressions ?? 0),
                num(r.gsc_clicks ?? 0),
                r.detail,
              ])}
            />
          </Panel>
        </>
      )}
    </SeoPage>
  );
}
